/**
 * D4 印刷履约看板（Phase 5-3 / Design §5.2 D4）
 *
 * KPI: 订单数 / 客单价(AOV) / 退款率
 * 切片: 日期范围 + 省份
 * 图表: 地区分布饼图（前 10） + 月份订单折线
 * 导出: CSV
 */
import { useState } from 'react';
import { Card, Row, Col, Statistic, Space, Result, Input } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { ChartContainer } from '../../components/dashboard/ChartContainer';
import { TimeSlicer, type TimeSlicerValue } from '../../components/dashboard/TimeSlicer';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { PERMISSIONS } from '../../contracts/permissions';
import { getPrintFulfillment, type PrintFulfillmentFilter } from '../../api/dashboard';

const DEFAULT_RANGE_DAYS = 90;
function defaultRange(): { startAt: string; endAt: string } {
  const end = new Date();
  const start = new Date(end.getTime() - DEFAULT_RANGE_DAYS * 24 * 3600 * 1000);
  return { startAt: start.toISOString(), endAt: end.toISOString() };
}

function PrintInner() {
  const initial = defaultRange();
  const [slicer, setSlicer] = useState<TimeSlicerValue>({
    startAt: initial.startAt,
    endAt: initial.endAt,
  });
  const [province, setProvince] = useState<string>('');

  const filter: PrintFulfillmentFilter = {
    startAt: slicer.startAt,
    endAt: slicer.endAt,
    province: province || undefined,
  };

  const query = useQuery({
    queryKey: ['dashboard', 'print', filter],
    queryFn: () => getPrintFulfillment(filter),
  });

  const data = query.data;

  // 地区分布饼图
  const regionOption = data
    ? {
        tooltip: { trigger: 'item' as const },
        legend: { type: 'scroll' as const, orient: 'horizontal' as const, bottom: 0 },
        series: [
          {
            name: '订单分布',
            type: 'pie' as const,
            radius: ['40%', '70%'],
            data: data.regionDistribution.slice(0, 10).map((r) => ({
              name: r.province,
              value: r.orderCount,
            })),
          },
        ],
      }
    : {};

  // 月份折线
  const monthOption = data
    ? {
        tooltip: { trigger: 'axis' as const },
        legend: { data: ['订单数', '收入(分)'] },
        xAxis: {
          type: 'category' as const,
          data: data.monthlySeries.map((m) => m.bucket),
        },
        yAxis: [
          { type: 'value' as const, name: '订单数' },
          { type: 'value' as const, name: '收入(分)' },
        ],
        series: [
          { name: '订单数', type: 'line' as const, data: data.monthlySeries.map((m) => m.orderCount) },
          {
            name: '收入(分)',
            type: 'line' as const,
            yAxisIndex: 1,
            data: data.monthlySeries.map((m) => m.revenue),
          },
        ],
      }
    : {};

  return (
    <div data-testid="dashboard-print">
      <Card
        title="D4 印刷履约看板"
        extra={<ExportButton kind="print" filter={filter} />}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <TimeSlicer value={slicer} onChange={setSlicer} showPeriod={false} />
            <Input
              placeholder="收货省份（如 北京市）"
              allowClear
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              style={{ width: 200 }}
              data-testid="print-province"
            />
          </Space>

          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="订单数"
                  value={data?.orderCount ?? 0}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="客单价 (分)"
                  value={Math.round(data?.aov ?? 0)}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="退款率"
                  value={data ? data.refundRate * 100 : 0}
                  precision={2}
                  suffix="%"
                  loading={query.isLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="退款金额 (分)"
                  value={data?.refundAmount ?? 0}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
          </Row>

          <ChartContainer
            title="地区分布 (前 10)"
            option={regionOption}
            loading={query.isLoading}
            empty={!data || data.regionDistribution.length === 0}
            testId="print-region"
          />
          <ChartContainer
            title="月份订单 / 收入"
            option={monthOption}
            loading={query.isLoading}
            empty={!data || data.monthlySeries.length === 0}
            testId="print-month"
          />
        </Space>
      </Card>
    </div>
  );
}

export default function PrintDashboardPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.DASHBOARD_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="数据看板需 dashboard:read 权限。"
        />
      }
    >
      <PrintInner />
    </PermissionGate>
  );
}
